import { signCreate, getCertList } from '../crypto/crypto';
import VipnetService from '../vipnet';
import SignatureTypes from 'constants/SignatureTypes';
import { moment } from '../../utils';
import { validateInn, validateInnLength } from '../../utils/reduxForm/validations';
import config from 'config';
import { VALIDATE_ERRORS } from '../../constants/crypto';
import { importCadesplugin_api } from 'services/crypto/crypto';

const vipnet = new VipnetService();
const currentDate = moment();
const DEFAULT_FILE_EXTENSION = '.doc'; //required for vipnet.
const CERTIFICATE_REGISTERED_STATUS = 'registered'; //required for vipnet.
const SIGN_FUNCTION_MAP = {
  [SignatureTypes.QES]: signCreate,
  [SignatureTypes.ITEX]: vipnet.signCreate.bind(vipnet),
};

export default class SignService {
  constructor({ signatureType, certificateHash, certificateStatus }) {
    this.signatureType = signatureType;
    this.certificateHash = certificateHash;
    this.certificateStatus = certificateStatus;
  }

  setOptions = (options) => {
    this.signatureType = options.signatureType ? options.signatureType : this.signatureType;
    this.certificateHash = options.certificateHash ? options.certificateHash : this.certificateHash;
  };

  async checkCertificate(inn) {
    if (this.certificateStatus && this.certificateStatus !== CERTIFICATE_REGISTERED_STATUS) {
      throw new Error('Сертификат не зарегистрирован!');
    }

    const certificate = await this.getCertificate();
    SignService.validateCertificate(certificate, inn, this.signatureType);

    return true;
  }

  async getCertificate() {
    const certificateList = await this.getCertificateList();
    switch (this.signatureType) {
      case SignatureTypes.ITEX:
        return certificateList[0];
      default:
        return certificateList.find(
          (certificate) => certificate.value.toLowerCase() === this.certificateHash.toLowerCase(),
        );
    }
  }

  /**
   *
   * @param {string|ArrayBuffer} dataToSign
   * @param {?object} options
   * @param {boolean} options.isEncodedData - dataToSign was encoded into base64
   * @param {string} options.fileName - file name to be signed
   * @returns {Promise.<void>}
   */
  async signCreate(dataToSign, options = {}) {
    const certificate = await this.getCertificateForSign();
    const signFunction = SignService.getSignFunction(this.signatureType);
    const signOptions = this.getSignOptions(options);

    return signFunction(certificate, dataToSign, signOptions);
  }

  getSignOptions(options = {}) {
    const fileNameArray = (options.fileName || '').split('.');
    const extension = fileNameArray.pop();
    const fileName = fileNameArray.join('.');
    switch (this.signatureType) {
      case SignatureTypes.ITEX:
        return {
          fileName: fileName || '',
          extension: extension || DEFAULT_FILE_EXTENSION,
          isEncodedData: options.isEncodedData,
        };
      default:
        return options.isEncodedData;
    }
  }

  async getCertificateList() {
    switch (this.signatureType) {
      case SignatureTypes.ITEX:
        return [await this.getVipnetCertificate()];
      default:
        return await getCertList();
    }
  }

  async getCertificateForSign() {
    switch (this.signatureType) {
      case SignatureTypes.ITEX:
        return await this.getVipnetCertificate();
      default:
        return this.certificateHash;
    }
  }

  /**
   * Check crypto functions existence
   * @param {string} inn - required for vipnet
   * @returns {Promise.<void>}
   */
  async checkCryptoFunctions(inn) {
    switch (this.signatureType) {
      case SignatureTypes.ITEX:
        return this.checkVipnetExistence(inn);
      default: {
        return await importCadesplugin_api();
      }
    }
  }

  async checkVipnetExistence(inn) {
    await vipnet.checkRegistration(inn);

    return await vipnet.checkConnection();
  }

  async getVipnetCertificate(skipValidation) {
    let certificate = this.certificate || (await vipnet.selectCertificate());
    const isInvalid =
      !skipValidation &&
      certificate &&
      certificate.Thumbprint.toLowerCase() !== this.certificateHash.toLowerCase();

    if (isInvalid) {
      const error = VALIDATE_ERRORS.UNKNOWN_CERTIFICATE[this.signatureType] || '';
      throw SignService.getErrorObject(error);
    }

    certificate.value = certificate.Thumbprint;
    this.certificate = certificate;

    return certificate;
  }

  clearCertificateCache = () => {
    this.certificate = null;
  };

  static getErrorObject(message) {
    return {
      friendlyErrorMessage: message,
    };
  }

  static getSignFunction(signatureType) {
    const signFunction = SIGN_FUNCTION_MAP[signatureType];

    if (!signFunction) {
      throw 'Unknown signature type';
    }

    return signFunction;
  }

  static validateCertificate(certificate, inn, signatureType = SignatureTypes.QES) {
    const hasValidInn =
      !config.isValidateCertificate ||
      (!validateInnLength(certificate.subjectObj.INN) && !validateInn(certificate.subjectObj.INN));

    if (!certificate.HasPrivateKey) {
      throw SignService.getErrorObject(VALIDATE_ERRORS.CONTROL_SUM);
    }

    if (certificate.ValidFromDate.isAfter(currentDate)) {
      throw SignService.getErrorObject(VALIDATE_ERRORS.TO_DATE);
    }

    if (certificate.ValidToDate.isBefore(currentDate)) {
      throw SignService.getErrorObject(VALIDATE_ERRORS.FROM_DATE);
    }

    if (certificate.subjectObj.OGRN) {
      throw SignService.getErrorObject(VALIDATE_ERRORS.HAS_OGRN);
    }

    if (!hasValidInn) {
      throw SignService.getErrorObject(VALIDATE_ERRORS.INN);
    }

    if (config.isValidateCertificate && certificate.subjectObj.INN !== inn) {
      throw SignService.getErrorObject(VALIDATE_ERRORS.USER_INN);
    }

    if (!certificate.isValid && signatureType === SignatureTypes.QES) {
      throw SignService.getErrorObject(VALIDATE_ERRORS.CERTIFICATE_CHAIN);
    }

    if (!certificate.hasGOSTAlgorithm) {
      throw SignService.getErrorObject(VALIDATE_ERRORS.IS_GOST);
    }
  }
}
